import { formatting } from "@/lib/formatting";
import {
  CandidacyEvaluationFollowUp,
  CompensationType,
  DrugScreenStatus,
  ShiftPreferenceStatus,
  ShiftType,
} from "@/types";

const SUSTAINABLE_COMMUTE_MAX_MINS = 45;
const WORK_HOURS_PER_YEAR = 2080;

const formatCommuteTime = (minutes: number) => {
  const wholeMinutes = Math.round(minutes);
  const hours = Math.floor(wholeMinutes / 60);
  const remainingMinutes = wholeMinutes % 60;
  if (!hours) return `${remainingMinutes.toString()} min`;
  return `${hours.toString()} hr ${remainingMinutes.toString()} min`;
};

export const candidacyEvaluations = {
  // Needs-follow-up mechanism for the logistics rows. Only known
  // conflicts flag — missing data already reads as "-" on the row.

  commuteNeedsFollowUp: ({
    commuteTimeMins,
  }: {
    commuteTimeMins?: number | null;
  }): boolean =>
    commuteTimeMins != null && commuteTimeMins > SUSTAINABLE_COMMUTE_MAX_MINS,

  drugAndBackgroundNeedsFollowUp: ({
    drugScreenStatus,
    hasCriminalRecord,
  }: {
    drugScreenStatus?: string | null;
    hasCriminalRecord?: boolean | null;
  }): boolean => {
    const isConcerningScreen =
      drugScreenStatus != null &&
      drugScreenStatus !== (DrugScreenStatus.WILL_PASS as string);
    return isConcerningScreen || hasCriminalRecord === true;
  },

  payNeedsFollowUp: ({
    compensationMax,
    compensationType,
    currentPay,
    desiredPay,
  }: {
    compensationMax?: number | null;
    compensationType?: CompensationType | null;
    currentPay?: number | null;
    desiredPay?: number | null;
  }): boolean => {
    // No stated ceiling or pay unit — nothing known to conflict.
    if (!compensationMax || compensationType == null) return false;
    const jobMaxHourly =
      compensationType === CompensationType.HOURLY
        ? compensationMax
        : compensationMax / WORK_HOURS_PER_YEAR;
    // Candidate pays are stored in whatever unit they were stated in. The
    // job's unit is explicit. Compare hourly-to-hourly. Desired pay is the
    // real ask, current pay stands in as the implied floor when there is no
    // desired — either exceeding the job's ceiling is a conversation though.
    return [desiredPay, currentPay].some((pay) => {
      if (pay == null) return false;
      const hourly = formatting.payIsHourly(pay)
        ? pay
        : pay / WORK_HOURS_PER_YEAR;
      return hourly > jobMaxHourly;
    });
  },

  shiftNeedsFollowUp: ({
    jobShifts,
    shiftPreferences,
  }: {
    jobShifts: ShiftType[];
    shiftPreferences: { shift: string; status: string }[];
  }): boolean => {
    // Only an explicit refusal of a shift the job runs flags — a shift the
    // candidate was never asked about is unknown, not a known conflict.
    return jobShifts.some((jobShift) =>
      shiftPreferences.some(
        (preference) =>
          preference.shift === (jobShift as string) &&
          preference.status === (ShiftPreferenceStatus.NO as string),
      ),
    );
  },

  getCommuteRecommendation: ({
    commuteDistanceMiles,
    commuteTimeMins,
  }: {
    commuteDistanceMiles?: number | null;
    commuteTimeMins?: number | null;
  }): CandidacyEvaluationFollowUp | null => {
    if (
      commuteTimeMins == null ||
      commuteTimeMins <= SUSTAINABLE_COMMUTE_MAX_MINS
    ) {
      return null;
    }

    const driveParts = [
      commuteDistanceMiles != null
        ? `${commuteDistanceMiles.toString()} mi`
        : null,
      formatCommuteTime(commuteTimeMins),
    ].filter(Boolean);

    return {
      note: "",
      subtitle: `Confirm whether a ${driveParts.join(" / ")} drive is sustainable long-term.`,
      title: "Commute sustainability",
    };
  },

  formatCommuteTime,
};
